/**
 * Story pool selection (step 11, 28-09-2026): which 2 of the 4 stories of the age group this session reads.
 *
 * The pick is random and not linked to a person. It is made once per assessment, kept for reloads
 * (sessionStorage), saved with the assessment (captureService.getAllData → `storyPool`) and restored when a
 * draft is resumed, so story 1, story 2 and their question pages always belong together.
 * Story ids are the same in every language; the language only decides which translation is shown.
 */
import { STORY_POOL_SLOTS, STORY_POOL_VERSION } from '../data/storyPool/storyPoolSlots';

export { STORY_POOL_VERSION } from '../data/storyPool/storyPoolSlots';

export interface StoryPoolSelection {
  poolVersion: number;
  tier: number;
  /** Story 1 and story 2 of this session (tier 1, age 5–7, only reads story 1). */
  storyIds: [string, string];
  pickedAt: string;
}

const STORAGE_KEY = 'dystest_story_pool';
let current: StoryPoolSelection | null = null;

function isSelection(value: unknown): value is StoryPoolSelection {
  const v = value as StoryPoolSelection | null;
  return !!v && typeof v === 'object' && typeof v.poolVersion === 'number' && typeof v.tier === 'number'
    && Array.isArray(v.storyIds) && v.storyIds.length === 2 && v.storyIds.every(id => typeof id === 'string')
    && typeof v.pickedAt === 'string';
}

function persist(selection: StoryPoolSelection | null): void {
  try {
    if (selection) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Reload survival is a convenience; the selection is also saved with the assessment.
  }
}

/** The selection of this session, or null when no story page has been reached yet. */
export function getStoryPoolSelection(): StoryPoolSelection | null {
  if (current) return current;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    current = isSelection(parsed) ? parsed : null;
  } catch {
    current = null;
  }
  return current;
}

/** Two different stories of the tier, in random order. */
export function pickPoolStories(tier: number, random: () => number = Math.random): [string, string] {
  const slots = [...(STORY_POOL_SLOTS[tier] ?? [])];
  for (let i = slots.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [slots[i], slots[j]] = [slots[j]!, slots[i]!];
  }
  return [slots[0]!, slots[1]!];
}

/**
 * The selection for this tier; picks and stores one if there is none yet, or if the stored one belongs to another
 * tier or pool version (a new assessment always starts without one, see clearStoryPoolSelection).
 */
export function ensureStoryPoolSelection(tier: number, random: () => number = Math.random): StoryPoolSelection | null {
  if (!STORY_POOL_SLOTS[tier]) return null;
  const existing = getStoryPoolSelection();
  if (existing && existing.tier === tier && existing.poolVersion === STORY_POOL_VERSION) return existing;
  current = { poolVersion: STORY_POOL_VERSION, tier, storyIds: pickPoolStories(tier, random), pickedAt: new Date().toISOString() };
  persist(current);
  return current;
}

/** Draft resume: put back the selection saved with the assessment. */
export function restoreStoryPoolSelection(value: unknown): void {
  current = isSelection(value) ? { ...value, storyIds: [value.storyIds[0], value.storyIds[1]] } : null;
  persist(current);
}

/** A new assessment starts without a selection. */
export function clearStoryPoolSelection(): void {
  current = null;
  persist(null);
}
